/**
 * Pure data models — no I/O, no side effects.
 */

import semver from "semver";

/**
 * Discriminator for catalog entries and asset references.
 * Used in asset ref strings (e.g. `'skill:my-skill'`, `'agent:my-agent'`).
 * @docLink packages/core/concepts#asset-kind
 */
export type AssetKind =
  | "skill"
  | "agent"
  | "prompt"
  | "bundle"
  | "flow"
  | "contract"
  | "mcp-server"
  | "persona" // identity fragment — replaces SOUL.md in composition
  | "ruleset" // behavioral constraints — replaces RULES.md in composition
  | "knowledge" // knowledge bundle directory
  | "connector"; // connector descriptor (CONNECTOR.md)

/**
 * All valid AssetKind values as a readonly array.
 * @docLink packages/core/concepts#asset-kinds
 */
export const ASSET_KINDS: readonly AssetKind[] = [
  "skill",
  "agent",
  "prompt",
  "bundle",
  "flow",
  "contract",
  "mcp-server",
  "persona",
  "ruleset",
  "knowledge",
  "connector",
];
/**
 * Asset kinds that represent individually installable assets (excludes "bundle").
 * Note: persona, ruleset, and knowledge are valid AssetKind values but are not
 * individually installable — they are resolved at session creation time via the
 * mixin-resolver, not through the install/deploy pipeline.
 * @docLink packages/core/concepts#individual-kinds
 */
export const INDIVIDUAL_KINDS = [
  "skill",
  "agent",
  "prompt",
  "flow",
  "contract",
  "mcp-server",
] as const satisfies AssetKind[];

// ── Dependency ────────────────────────────────────────────────────────────────

/**
 * Declared dependency on another catalog asset.
 * @docLink packages/core/concepts#dependency
 */
export interface Dependency {
  /** Asset kind (e.g. "skill", "agent", "contract"). */
  kind: string;
  /** Asset name within that kind. */
  name: string;
  /** Canonical publisher namespace (when known). */
  publisher?: string;
}

/**
 * Parse a `"kind:name"` dependency string, or a bare `"name"` (defaults kind to `"skill"`).
 *
 * @param s - Dependency string, e.g. `"skill:use-exa"` or `"use-exa"`
 * @returns Parsed `{ kind, name }` dependency object
 * @docLink packages/core/concepts#parse-dep
 */
export function parseDep(s: string): Dependency {
  const i = s.indexOf(":");
  if (i !== -1) {
    const kind = s.slice(0, i).trim();
    const name = s.slice(i + 1).trim();
    return { kind, name };
  }
  return { kind: "skill", name: s.trim() };
}

/**
 * Serialize a Dependency back to its `"kind:name"` string form.
 *
 * @param d - Dependency to serialize
 * @returns String in `"kind:name"` format
 * @docLink packages/core/concepts#dep-to-str
 */
export function depToStr(d: Dependency): string {
  return `${d.kind}:${d.name}`;
}

// ── CatalogEntry ──────────────────────────────────────────────────────────────

/**
 * A resolved catalog entry describing an installable asset.
 * Produced by manifest parsers and stored in the catalog YAML.
 * @docLink packages/core/concepts#catalog-entry
 */
export interface CatalogEntry {
  /** Unique asset name within its kind. */
  name: string;
  /** Asset kind discriminator. */
  kind: AssetKind;
  /** Short description of what this asset does. */
  description: string;
  /** Absolute path to manifest file */
  source: string;
  /** Canonical publisher namespace this asset belongs to (formerly `repository`). */
  publisher?: string;
  /**
   * `"<org>/<repo>"` coordinate the asset came from. Absent → the asset groups
   * directly under its publisher (local library with no git remote, or a
   * non-GitHub source). Replaces the former derived `domain` axis.
   */
  repo?: string;
  /**
   * Navigational domain slug (from a sidecar `DOMAIN.md`), or absent when no
   * domain claims this asset. Display-only — groups assets under their publisher
   * in the manage TUI; never part of identity or the install layout.
   */
  domainSlug?: string;
  /** Semantic version string (may be empty if not declared in the manifest). */
  version: string;
  /** Skills, agents, or other assets this entry depends on. */
  requires: Dependency[];
  /** Bundle dependencies — asset refs to install (bundles only). */
  dependencies: string[];
  /**
   * Kind-specific metadata extracted from the manifest frontmatter.
   * Used by MCP servers to carry default config (transport, command, args, env, url, headers)
   * so that `mcp:name` dependency refs resolve to a runnable declaration without
   * requiring verbose inline config in `skaile.yaml`.
   */
  metadata?: Record<string, unknown>;
  /** Curated facet, surfaced as a badge in the manage TUI (not a tree axis). */
  category?: string;
  /** Store-listing keywords (manifest `assets[].keywords`, else frontmatter). */
  keywords?: string[];
  /** Store-listing license (manifest `assets[].license`, else repo-level default). */
  license?: string;
  /** Store-listing homepage (manifest `assets[].homepage`, else repo-level default). */
  homepage?: string;
}

/**
 * How the members of a domain relate — drives display affordances only, never
 * install behaviour. Single canonical spelling shared with the store
 * (`DomainRelation` enum) and forge contract.
 */
export type DomainRelation = "additions" | "alternatives" | "sequence";

/**
 * Display-only metadata for a navigational domain (from a sidecar `DOMAIN.md`,
 * surfaced by the store's `catalog.listDomains`). Keyed by {@link CatalogEntry.domainSlug}.
 * A domain groups assets under their publisher for co-discovery; it is never
 * part of asset identity and never bulk-installs.
 */
export interface CatalogDomain {
  /** Stable slug — the join key to {@link CatalogEntry.domainSlug}. */
  slug: string;
  /** Human-readable title (may change without breaking the slug join). */
  title: string;
  /** Free-text description from the `DOMAIN.md` body. */
  description?: string;
  /** How members relate — drives sort + per-asset glyph, not installs. */
  relation?: DomainRelation;
  /** Display order among a publisher's domains (ascending). */
  order?: number;
  /** `kind:name` ref of the recommended member (alternatives domains). */
  recommendedRef?: string;
  /** Sidecar provenance — `"<org>/<repo>"` the `DOMAIN.md` was authored in. */
  sidecarSource?: string;
}

/**
 * Construct a CatalogEntry from a raw deserialized record (e.g. from catalog.yaml).
 *
 * @param d - Raw object with loosely typed fields
 * @returns Normalized CatalogEntry
 * @docLink packages/core/concepts#entry-from-raw
 */
export function entryFromRaw(d: Record<string, unknown>): CatalogEntry {
  const rawDeps = d.dependencies;
  const deps = Array.isArray(rawDeps) ? (rawDeps as string[]).filter(Boolean) : [];
  const entry: CatalogEntry = {
    name: String(d.name ?? ""),
    kind: String(d.kind ?? "skill") as AssetKind,
    description: String(d.description ?? ""),
    source: String(d.source ?? ""),
    publisher: d.publisher != null ? String(d.publisher) : undefined,
    // Read `repo`, falling back to the legacy `domain` key so a stale
    // catalog.yaml cache written before this release still groups sanely.
    // The cache self-heals on next write (entryToRaw stops writing `domain`).
    repo: d.repo != null ? String(d.repo) : d.domain != null ? String(d.domain) : undefined,
    domainSlug: d.domainSlug != null ? String(d.domainSlug) : undefined,
    version: String(d.version ?? ""),
    requires: Array.isArray(d.requires)
      ? (d.requires as string[]).filter(Boolean).map(parseDep)
      : [],
    dependencies: deps,
  };
  if (d.metadata != null && typeof d.metadata === "object" && !Array.isArray(d.metadata)) {
    entry.metadata = d.metadata as Record<string, unknown>;
  }
  if (d.category != null) entry.category = String(d.category);
  if (Array.isArray(d.keywords)) {
    entry.keywords = (d.keywords as unknown[]).filter((k): k is string => typeof k === "string");
  }
  if (d.license != null) entry.license = String(d.license);
  if (d.homepage != null) entry.homepage = String(d.homepage);
  return entry;
}

/**
 * Serialize a CatalogEntry to a plain record suitable for YAML/JSON persistence.
 *
 * @param e - CatalogEntry to serialize
 * @returns Plain object with string-serialized requires and dependencies
 * @docLink packages/core/concepts#entry-to-raw
 */
export function entryToRaw(e: CatalogEntry): Record<string, unknown> {
  const raw: Record<string, unknown> = {
    name: e.name,
    kind: e.kind,
    description: e.description,
    source: e.source,
    publisher: e.publisher,
    repo: e.repo,
    version: e.version,
    ...(e.domainSlug != null ? { domainSlug: e.domainSlug } : {}),
    requires: e.requires.map(depToStr),
    dependencies: e.dependencies,
  };
  if (e.metadata) raw.metadata = e.metadata;
  if (e.category != null) raw.category = e.category;
  if (e.keywords && e.keywords.length > 0) raw.keywords = e.keywords;
  if (e.license != null) raw.license = e.license;
  if (e.homepage != null) raw.homepage = e.homepage;
  return raw;
}

// ── AssetRef ─────────────────────────────────────────────────────────────────

/**
 * A parsed asset reference: `kind:@<publisher>/name[#pin]`.
 *
 * `publisher` is the canonical, GitHub-shaped publisher namespace (≤39 chars,
 * alphanumeric+hyphen). `pin` is a SemVer constraint, exact SHA, or absent.
 * Floating refs (`main`, `latest`, `HEAD`) are rejected at parse time.
 *
 * Produced by `parseAssetRef`; consumed by `resolveAsset` and `resolveAll`.
 * @docLink packages/core/concepts#asset-ref
 */
export interface AssetRef {
  kind: string;
  name: string;
  /** Canonical publisher namespace. Optional only for ambiguity-resolution
   *  during migration; new manifests require it. */
  publisher?: string;
  /** SemVer constraint (^1.4.0, ~1.4, 1.x, exact 1.4.0), 40-char SHA, or undefined. */
  pin?: string;
}

const SHA_RE = /^[0-9a-f]{40}$/i;

// Scoped grammar: `kind:@publisher/name[#version]`. The publisher sub-pattern is
// GitHub-shaped (1-39 chars, alphanumeric + single hyphens, no leading/trailing or
// double hyphen); `@` is the scope sigil and `#` the version sigil. `name` is
// intentionally permissive (`[^/#\s]+` — only the structural separators are
// forbidden); floating-ref rejection stays in `isValidPin`, so `#main`/`#latest`
// match here and fail there with a clear message.
const SCOPED_REF_RE =
  /^(?:([a-z][a-z0-9-]*):)?@([A-Za-z0-9](?:[A-Za-z0-9]|-(?!-)){0,37}[A-Za-z0-9]|[A-Za-z0-9])\/([^/#\s]+)(?:#(.+))?$/;

// Canonical asset-name grammar: lowercase kebab-case (npm-style, scope-free) —
// `a-z` / `0-9` segments joined by single hyphens, no leading/trailing hyphen,
// no uppercase, spaces, dots, or underscores. Names become directory names,
// lock keys, and CLI args, so the constraint is intentionally narrow.
const ASSET_NAME_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

// Diagnostic-only matcher: a ref carrying a `kind:@publisher/` scope prefix,
// capturing whatever follows (sans `#pin`) as the candidate name. Used solely
// to tell "missing publisher" apart from "malformed name" when the strict
// grammar above fails (e.g. a space in the name).
const SCOPE_PREFIX_RE = /^(?:[a-z][a-z0-9-]*:)?@[^/\s]+\/([^#]+?)(?:#.*)?$/;

/** Human-readable statement of the canonical asset-name rule. */
export const ASSET_NAME_HINT =
  'names must be lowercase kebab-case (a-z, 0-9, single hyphens; e.g. "cli-concept")';

/**
 * True when `name` is a canonical asset name: lowercase kebab-case, scope-free.
 *
 * This is the single source of truth for asset-name validity — `parseAssetRef`
 * enforces it, and `skaile validate` reports violations across a repo tree.
 * @docLink packages/core/concepts#is-valid-asset-name
 */
export function isValidAssetName(name: string): boolean {
  return ASSET_NAME_RE.test(name);
}

/**
 * Coerce an arbitrary string into a canonical asset name (lowercase kebab-case).
 *
 * For **third-party sources we don't control**, an upstream asset may carry a
 * human-styled identity (`"Complex App"`, `"CLI_Concept"`). The canonical grammar
 * (`isValidAssetName`) rejects those, so the ingestion boundary slugifies them to
 * a valid identity rather than crashing: Unicode is folded to ASCII, the result is
 * lowercased, every run of non-`[a-z0-9]` becomes a single hyphen, and
 * leading/trailing hyphens are trimmed. The transform is idempotent and a no-op on
 * already-canonical names.
 *
 * This is intentionally **lossy** — the pretty original is not part of identity.
 * Callers that want to preserve it for display must stash it themselves (e.g. on
 * `CatalogEntry.metadata.displayName`). Authoring-time validation (`skaile validate`)
 * still uses `isValidAssetName` on raw frontmatter, so first-party typos surface there.
 *
 * @throws When `raw` contains no `[a-z0-9]` character (nothing to slugify).
 * @docLink packages/core/concepts#slugify-asset-name
 */
export function slugifyAssetName(raw: string): string {
  const slug = raw
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "") // strip combining accents
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  if (!slug) {
    throw new Error(`cannot derive a canonical asset name from "${raw}" — ${ASSET_NAME_HINT}.`);
  }
  return slug;
}

/** True when `pin` is a SemVer constraint, exact SemVer, or 40-char SHA. */
function isValidPin(pin: string): boolean {
  if (SHA_RE.test(pin)) return true;
  if (semver.valid(pin)) return true;
  if (semver.validRange(pin)) return true;
  return false;
}

/** Throw the canonical non-canonical-pin error for a ref `s` carrying `pin`. */
function rejectFloatingPin(s: string, pin: string): never {
  throw new Error(
    `non-canonical pin "${pin}" in "${s}" — pin must be a SemVer ` +
      "constraint (1.4.0, ^1.4, ~1.4, 1.x), a 40-char SHA, or absent. " +
      "Floating refs (main/latest/HEAD) are not allowed.",
  );
}

/** Throw the canonical malformed-name error for a ref `s` carrying `name`. */
function rejectInvalidName(s: string, name: string): never {
  throw new Error(
    `invalid asset name "${name}" in ref "${s}" — ${ASSET_NAME_HINT}. ` +
      "Run the `migrate-skaile-manifest` skill on legacy manifests.",
  );
}

/**
 * Parse a canonical asset reference string into an `AssetRef` object.
 *
 * Grammar: `kind:@<publisher>/name[#version]`
 *   "skill:@skaile-ai/audit"          → { kind: "skill", name: "audit", publisher: "skaile-ai" }
 *   "skill:@skaile-ai/audit#^1.4.0"   → { …, pin: "^1.4.0" }
 *   "skill:@skaile-ai/audit#<40-sha>" → { …, pin: "<40-sha>" }
 *
 * `@` is the scope sigil and `#` the version sigil. `<publisher>` is GitHub-shaped
 * (≤39 chars, alphanumeric+hyphen, no leading/trailing or double hyphen). `pin`
 * accepts a SemVer constraint (`^`, `~`, `x`, exact), a 40-char SHA, or is absent.
 * Floating refs (`main`/`latest`/`HEAD`/anything non-canonical) are parse-time errors.
 *
 * The shorthand `mcp:` is normalized to `mcp-server:`. `kind` defaults to `"skill"`.
 *
 * @param s - Asset reference string
 * @returns Parsed `AssetRef`
 * @throws When the ref matches no grammar, the publisher is malformed, or the pin
 *   is a floating ref.
 * @docLink packages/core/concepts#parse-asset-ref
 */
export function parseAssetRef(s: string): AssetRef {
  const trimmed = s.trim();
  const m = SCOPED_REF_RE.exec(trimmed);
  if (m) {
    const kindRaw = m[1] ?? "skill";
    const kind = kindRaw === "mcp" ? "mcp-server" : kindRaw;
    const publisher = m[2];
    const name = m[3];
    const pin = m[4]?.trim() || undefined;
    if (!isValidAssetName(name)) rejectInvalidName(s, name);
    if (pin !== undefined && !isValidPin(pin)) rejectFloatingPin(s, pin);
    return { kind, name, publisher, pin };
  }

  // The strict grammar failed. If the ref nonetheless carries a `@publisher/`
  // scope and the fault is the name (e.g. an embedded space), surface a
  // name-specific error rather than the misleading "publisher required" — the
  // publisher is plainly present. A *valid* captured name means the publisher
  // itself is malformed, so fall through to the publisher error below.
  const scoped = SCOPE_PREFIX_RE.exec(trimmed);
  if (scoped) {
    const candidate = scoped[1].trim();
    if (!isValidAssetName(candidate)) rejectInvalidName(s, candidate);
  }

  throw new Error(
    `publisher required in asset ref "${s}" — use \`kind:@<publisher>/name\`. ` +
      "Run the `migrate-skaile-manifest` skill on legacy manifests.",
  );
}

/**
 * Serialize an AssetRef back to its canonical string form.
 *
 * Emits the scope form `kind:@publisher/name[#pin]` when `publisher` is present,
 * and the bare `kind:name[#pin]` form for transitive refs that inherit it.
 *
 * @param ref - AssetRef to serialize
 * @returns String in `"kind:@publisher/name[#pin]"` (or bare `"kind:name[#pin]"`) format
 * @docLink packages/core/concepts#asset-ref-to-str
 */
export function assetRefToStr(ref: AssetRef): string {
  const scope = ref.publisher ? `@${ref.publisher}/${ref.name}` : ref.name;
  return `${ref.kind}:${scope}${ref.pin ? `#${ref.pin}` : ""}`;
}

/**
 * Convert an AssetRef to a simple Dependency, dropping the repository and pin qualifiers.
 *
 * @param ref - Full asset reference (may include repository and pin)
 * @returns Dependency with only kind and name
 * @docLink packages/core/concepts#asset-ref-to-dep
 */
export function assetRefToDep(ref: AssetRef): Dependency {
  // Grammar-agnostic — drops publisher/pin, so the scope-sigil change doesn't touch it.
  return { kind: ref.kind, name: ref.name };
}

// ── Lock File ────────────────────────────────────────────────────────────────

/** A path/sha256 pair recorded for one file inside a locked asset. */
export interface LockFileEntry {
  path: string;
  sha256: string;
}

/**
 * Per-asset entry in the lock file (v3 schema).
 * Keyed in `LockFile.assets` by canonical ref (`<kind>:@<publisher>/<name>#<version>`).
 * @docLink packages/core/api-reference#lock-entry
 */
export interface LockEntry {
  /** Composite sha256 over sorted `<path>:<sha256>\n` rows. */
  sha256: string;
  /** Resolved upstream source + commit. */
  source: { url: string; commit: string };
  /** Files that constitute the asset. */
  files: LockFileEntry[];
  /** True when an `overrides[]` entry pinned this resolution. */
  override_applied: boolean;
}

/** Per-source-URL entry in `LockFile.sources`. */
export interface LockSourceEntry {
  url: string;
  commit: string;
}

/**
 * Full skaile.lock.yaml structure (v3 schema).
 * @docLink packages/core/api-reference#lock-file
 */
export interface LockFile {
  /** Lock file schema version. v3 is canonical. */
  schema_version: 3;
  /** ISO timestamp of when the lock was generated. */
  locked_at: string;
  /** Resolved assets keyed by canonical ref `<kind>:@<publisher>/<name>#<version>`. */
  assets: Record<string, LockEntry>;
  /** Every source URL that contributed at least one resolved asset. */
  sources: LockSourceEntry[];
  /**
   * Resolved plugin packages (from `skaile.yaml` `plugins:`), keyed by package
   * name. The plugin-store reconciler reads this slice into its reconcile-hash
   * so a lock change reinstalls even when the manifest list is unchanged.
   */
  plugins?: Record<string, LockPluginEntry>;
}

/** One pinned plugin package in the lock file's `plugins` slice. */
export interface LockPluginEntry {
  /** Resolved exact version (e.g. "0.1.3"). */
  version: string;
  /** Package integrity hash (e.g. "sha512-..."); may be empty until populated from bun's output. */
  integrity: string;
}

// ── AgentManifest ────────────────────────────────────────────────────────────

/**
 * Agent package manifest — parsed from `agent.yaml` in an agent directory.
 * Defines the agent's identity, model preferences, capability requirements, and composition mixins.
 * @docLink packages/core/concepts#agent-manifest
 */
export interface AgentManifest {
  spec_version?: string;
  name?: string;
  version?: string;
  description?: string;
  model?: {
    preferred?: string;
    fallback?: string[];
    constraints?: { temperature?: number; max_tokens?: number };
  };
  tools?: {
    allowed?: string[];
    denied?: string[];
  };
  delegation?: { mode?: string };
  requires?: Array<{ name: string; source: string; version?: string; mount: string }>;
  tags?: string[];
  author?: string;
  metadata?: Record<string, unknown>;
  /**
   * @deprecated Use `persona`, `rules`, and `knowledge` mixin arrays instead.
   * This field is declared but was never resolved at runtime.
   */
  extends?: string;
  abilities?: string[];
  /**
   * Identity fragments to compose into the agent persona.
   * Each entry is a catalog ref (`persona:name@repo#pin`), a bare name
   * (defaults to kind "persona"), or a local path (`./my.persona.md`).
   * Resolved in order — concatenated with \n\n---\n\n separator.
   */
  persona?: string[];
  /**
   * Behavioral rule sets to compose into the agent's constraints.
   * Each entry is a catalog ref (`ruleset:name@repo#pin`), bare name,
   * or local path. All rules apply (union).
   */
  rules?: string[];
  /**
   * Knowledge bundles to compose into the agent's domain knowledge.
   * Each entry is a catalog ref (`knowledge:name@repo#pin`), bare name,
   * or local path to a knowledge directory. Loaded in declaration order.
   */
  knowledge?: string[];
  contracts?: string[];
  /**
   * v2 composition items. Each entry references an asset (skill, connector,
   * soul, ruleset, etc.) with binding semantics (discoverable, inline-live,
   * inline-snapshot). Resolved at session start via the composition module.
   */
  composes?: Array<{
    kind: string;
    ref: string;
    instance?: string;
    binding?: "discoverable" | "inline-live" | "inline-snapshot";
  }>;
  runtime?: {
    max_turns?: number;
    timeout?: number;
    [key: string]: unknown;
  };
}

// ── Repository ───────────────────────────────────────────────────────────────

type RepositoryKind = "local" | "github";

/**
 * A registered asset repository (local path or cloned GitHub repo).
 * Stored in the catalog; consumed by `repo-manager` for cloning and scanning.
 * @docLink packages/core/concepts#repository
 */
export interface Repository {
  /** Logical repository name. */
  name: string;
  /** Storage kind: "local" for paths, "github" for remote URLs. */
  kind: RepositoryKind;
  /** Absolute path (local) or remote URL (github). */
  path: string;
  /** Active branch. */
  branch: string;
  /** Upstream fork URL (for fork-based workflows). */
  upstream?: string;
}

/**
 * Construct a `Repository` from a raw deserialized record.
 *
 * @param d - Raw object from YAML/JSON
 * @returns Normalized `Repository`
 * @docLink packages/core/concepts#repository-from-raw
 */
export function repositoryFromRaw(d: Record<string, unknown>): Repository {
  return {
    name: String(d.name ?? ""),
    kind: String(d.kind ?? "local") as RepositoryKind,
    path: String(d.path ?? ""),
    branch: String(d.branch ?? "main"),
    upstream: d.upstream != null ? String(d.upstream) : undefined,
  };
}

/**
 * Serialize a `Repository` to a plain record for YAML/JSON persistence.
 * Omits the `branch` field when it equals `"main"` to keep serialized output clean.
 *
 * @param r - Repository to serialize
 * @returns Plain object suitable for YAML/JSON
 * @docLink packages/core/concepts#repository-to-raw
 */
export function repositoryToRaw(r: Repository): Record<string, unknown> {
  const d: Record<string, unknown> = {
    name: r.name,
    kind: r.kind,
    path: r.path,
  };
  if (r.branch !== "main") d.branch = r.branch;
  if (r.upstream != null) d.upstream = r.upstream;
  return d;
}
